import React from 'react';
import { getEntityId } from 'helpers/entityId';
import { Button, Col, Form, Modal, Row, Badge } from 'react-bootstrap';
import { Employee } from 'data/users';
import { OnCallAssignment } from 'services/api/callout';
import { toast } from 'react-hot-toast';
import { ColorConfig, PRESET_COLORS, SubstituteState } from '../types';
import { colorToCss } from '../utils/colors';

export const SubstituteAssigneeModal = ({
  show,
  onHide,
  substitute,
  setSubstitute,
  substituteColor,
  setSubstituteColor,
  sortedAssignments,
  callOutEligible,
  onSubmit,
  isSaving
}: {
  show: boolean;
  onHide: () => void;
  substitute: SubstituteState;
  setSubstitute: React.Dispatch<React.SetStateAction<SubstituteState>>;
  substituteColor: ColorConfig;
  setSubstituteColor: React.Dispatch<React.SetStateAction<ColorConfig>>;
  sortedAssignments: OnCallAssignment[];
  callOutEligible: Employee[];
  onSubmit: () => void;
  isSaving: boolean;
}) => {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Substitute assignee</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={e => {
            e.preventDefault();
            if (
              !substitute.assignmentId ||
              !substitute.replaceUserId ||
              !substitute.substituteUserId ||
              !substitute.startDate ||
              !substitute.endDate
            ) {
              toast.error('Fill out all substitution fields');
              return;
            }
            onSubmit();
          }}
        >
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>Replace</Form.Label>
              <Form.Select
                value={substitute.replaceUserId}
                onChange={e =>
                  setSubstitute(prev => ({
                    ...prev,
                    replaceUserId: e.target.value
                  }))
                }
              >
                <option value="">Select assignee</option>
                {sortedAssignments
                  .find(a => getEntityId(a) === substitute.assignmentId)
                  ?.assignees.map(u => (
                    <option key={getEntityId(u)} value={getEntityId(u) || ''}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
              </Form.Select>
            </Col>

            <Col xs={12}>
              <Form.Label>With</Form.Label>
              <Form.Select
                value={substitute.substituteUserId}
                onChange={e =>
                  setSubstitute(prev => ({
                    ...prev,
                    substituteUserId: e.target.value
                  }))
                }
              >
                <option value="">Select pool user</option>
                {callOutEligible.map(u => (
                  <option key={getEntityId(u)} value={getEntityId(u) || ''}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label>Start</Form.Label>
              <Form.Control
                type="datetime-local"
                value={substitute.startDate}
                onChange={e =>
                  setSubstitute(prev => ({
                    ...prev,
                    startDate: e.target.value
                  }))
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>End</Form.Label>
              <Form.Control
                type="datetime-local"
                value={substitute.endDate}
                onChange={e =>
                  setSubstitute(prev => ({
                    ...prev,
                    endDate: e.target.value
                  }))
                }
              />
            </Col>

            <Col xs={12}>
              <Form.Label>Substitute color</Form.Label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Badge
                  pill
                  bg={undefined}
                  style={{
                    backgroundColor: colorToCss(substituteColor),
                    border: '1px solid rgba(0,0,0,0.15)'
                  }}
                >
                  Preview
                </Badge>
                <Form.Select
                  style={{ maxWidth: 200 }}
                  value={
                    substituteColor.mode === 'preset'
                      ? substituteColor.presetKey
                      : 'custom'
                  }
                  onChange={e => {
                    const v = e.target.value;
                    if (v === 'custom') {
                      setSubstituteColor({
                        mode: 'custom',
                        r: 255,
                        g: 0,
                        b: 0
                      });
                      return;
                    }
                    setSubstituteColor({
                      mode: 'preset',
                      presetKey: v as any
                    });
                  }}
                >
                  {Object.entries(PRESET_COLORS).map(([k, p]) => (
                    <option key={k} value={k}>
                      {p.label}
                    </option>
                  ))}
                  <option value="custom">Custom (RGB)</option>
                </Form.Select>
                {substituteColor.mode === 'custom' && (
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={255}
                      value={substituteColor.r}
                      style={{ width: 90 }}
                      onChange={e =>
                        setSubstituteColor(prev => ({
                          ...prev,
                          r: Number(e.target.value)
                        }))
                      }
                    />
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={255}
                      value={substituteColor.g}
                      style={{ width: 90 }}
                      onChange={e =>
                        setSubstituteColor(prev => ({
                          ...prev,
                          g: Number(e.target.value)
                        }))
                      }
                    />
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={255}
                      value={substituteColor.b}
                      style={{ width: 90 }}
                      onChange={e =>
                        setSubstituteColor(prev => ({
                          ...prev,
                          b: Number(e.target.value)
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save substitution'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
